<script setup lang="ts">
import { DEFAULT_GRID_SIZE } from '~/lib/seatmapeditor/constants'

/*
	Grid.vue

	This is the viewport/camera system. It creates the infinite grid background 
	and handles all the "Camera" math (Zooming, Panning, and Touch Pinching).
 */

type GridLine = {
	points: number[]
}

const props = withDefaults(
	defineProps<{
		gridSize?: number
		// Per-axis pitch, defaults to gridSize. Kept separate because imported seat
		// maps can have a different horizontal (seat) spacing than vertical (row)
		// spacing, and the grid lines need to match each independently.
		gridSizeX?: number
		gridSizeY?: number
		// Phase-shifts grid lines so they line up with seat/row snap positions.
		gridOffsetX?: number
		gridOffsetY?: number
		background?: string
		majorStroke?: string
		showGrid?: boolean
		showGridLines?: boolean
		panEnabled?: boolean
	}>(),
	{
		gridSize: DEFAULT_GRID_SIZE,
		gridSizeX: DEFAULT_GRID_SIZE,
		gridSizeY: DEFAULT_GRID_SIZE,
		gridOffsetX: 0,
		gridOffsetY: 0,
		background: '#ffffff',
		majorStroke: '#9aa8ba99',
		showGrid: true,
		showGridLines: undefined,
		panEnabled: true
	}
)

const emit = defineEmits<{
	(e: 'camera-change', payload: { x: number; y: number; scale: number; viewportWidth: number; viewportHeight: number }): void
	(e: 'canvas-click', payload: { x: number; y: number }): void
	(e: 'canvas-pointerdown', payload: { x: number; y: number }): void
	(e: 'canvas-pointermove', payload: { x: number; y: number }): void
	(e: 'canvas-pointerup', payload: { x: number; y: number }): void
}>()

const { background, majorStroke } = toRefs(props)
const showGridVisible = computed(() => props.showGridLines ?? props.showGrid)
const hostEl = ref<HTMLElement | null>(null)
const stageWidth = ref(0)
const stageHeight = ref(0)
const cameraX = ref(0)
const cameraY = ref(0)
const cameraScale = ref(1)
const isPanning = ref(false)
const panStart = ref({ x: 0, y: 0 })
const panOrigin = ref({ x: 0, y: 0 })

const zoom = {
	min: 0.3,
	max: 4,
	step: 1.08
}

const touchState = {
	lastCenter: null as { x: number; y: number } | null,
	lastDistance: 0
}

let resizeObserver: ResizeObserver | null = null
let cameraEmitRafId: number | null = null
const scheduleFrame = (callback: FrameRequestCallback) => {
	if (typeof requestAnimationFrame === 'function') {
		return requestAnimationFrame(callback)
	}

	return setTimeout(() => callback(Date.now()), 16) as unknown as number
}

const cancelScheduledFrame = (frameId: number) => {
	if (typeof cancelAnimationFrame === 'function') {
		cancelAnimationFrame(frameId)
		return
	}

	clearTimeout(frameId)
}

const emitCameraChange = () => {
	emit('camera-change', {
		x: cameraX.value,
		y: cameraY.value,
		scale: cameraScale.value,
		viewportWidth: stageWidth.value,
		viewportHeight: stageHeight.value
	})
}

const scheduleCameraChangeEmit = () => {
	if (cameraEmitRafId !== null) {
		return
	}

	cameraEmitRafId = scheduleFrame(() => {
		cameraEmitRafId = null
		emitCameraChange()
	})
}

const syncStageSize = () => {
	if (!hostEl.value) {
		return
	}

	const rect = hostEl.value.getBoundingClientRect()
	stageWidth.value = Math.max(1, Math.floor(rect.width))
	stageHeight.value = Math.max(1, Math.floor(rect.height))
}

// This component already only ever runs client-side (the whole editor is nested
// inside an ancestor `<ClientOnly>` and only registered via a `.client.ts` plugin),
// so `<v-stage>` doesn't need its own `<ClientOnly>` wrapper for SSR safety. We still
// gate it behind a plain `isMounted` flag (rather than nesting another `<ClientOnly>`)
// so Konva's Stage only mounts once `hostEl` is available, without introducing a
// second Suspense/ClientOnly boundary that can conflict with the outer one.
const isMounted = ref(false)

onMounted(() => {
	isMounted.value = true
	syncStageSize()

	if (hostEl.value) {
		resizeObserver = new ResizeObserver(syncStageSize)
		resizeObserver.observe(hostEl.value)
	}

	window.addEventListener('resize', syncStageSize)
})

onBeforeUnmount(() => {
	resizeObserver?.disconnect()
	window.removeEventListener('resize', syncStageSize)
	if (cameraEmitRafId !== null) {
		cancelScheduledFrame(cameraEmitRafId)
		cameraEmitRafId = null
	}
})

const getClientPoint = (event: any) => {
	const touch = event?.evt?.touches?.[0]
	if (touch) {
		return { x: touch.clientX, y: touch.clientY }
	}

	return {
		x: event.evt.clientX,
		y: event.evt.clientY
	}
}

const clampScale = (nextScale: number) => {
	return Math.max(zoom.min, Math.min(zoom.max, nextScale))
}

const zoomAtPoint = (point: { x: number; y: number }, nextScale: number) => {
	const oldScale = cameraScale.value
	const worldPoint = {
		x: (point.x - cameraX.value) / oldScale,
		y: (point.y - cameraY.value) / oldScale
	}

	cameraScale.value = nextScale
	cameraX.value = point.x - worldPoint.x * nextScale
	cameraY.value = point.y - worldPoint.y * nextScale
}

const getTouchCenter = (touch1: Touch, touch2: Touch) => {
	return {
		x: (touch1.clientX + touch2.clientX) / 2,
		y: (touch1.clientY + touch2.clientY) / 2
	}
}

const getTouchDistance = (touch1: Touch, touch2: Touch) => {
	return Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY)
}

const onWheel = (event: any) => {
	event.evt.preventDefault()
	const pointer = event.target.getStage().getPointerPosition()

	if (!pointer) {
		return
	}

	let direction = event.evt.deltaY > 0 ? -1 : 1

	if (event.evt.ctrlKey) {
		direction = -direction
	}

	const nextScale = clampScale(cameraScale.value * (direction > 0 ? zoom.step : 1 / zoom.step))
	zoomAtPoint(pointer, nextScale)
}

const onPanStart = (event: any) => {
	if (!props.panEnabled) {
		isPanning.value = false
		return
	}

	if (event?.evt?.touches?.length === 2) {
		return
	}

	isPanning.value = true
	panStart.value = getClientPoint(event)
	panOrigin.value = {
		x: cameraX.value,
		y: cameraY.value
	}
}

const onPointerDown = (event: any) => {
	const stage = event?.target?.getStage?.()
	const pointer = stage?.getPointerPosition?.()
	const worldPoint = {
		x: pointer ? (pointer.x - cameraX.value) / cameraScale.value : 0,
		y: pointer ? (pointer.y - cameraY.value) / cameraScale.value : 0
	}

	if (event?.target === stage || event?.target?.attrs?.name === 'grid-background') {
		if (!props.panEnabled) {
			emit('canvas-pointerdown', worldPoint)
		}
		emit('canvas-click', worldPoint)
	}

	onPanStart(event)
}

const onPanMove = (event: any) => {
	const stage = event?.target?.getStage?.()
	const pointer = stage?.getPointerPosition?.()
	if (!props.panEnabled && pointer) {
		emit('canvas-pointermove', {
			x: (pointer.x - cameraX.value) / cameraScale.value,
			y: (pointer.y - cameraY.value) / cameraScale.value
		})
	}

	if (!isPanning.value || event?.evt?.touches?.length === 2) {
		return
	}

	event.evt.preventDefault()
	const point = getClientPoint(event)

	cameraX.value = panOrigin.value.x + (point.x - panStart.value.x)
	cameraY.value = panOrigin.value.y + (point.y - panStart.value.y)
}

const onPanEnd = (event?: any) => {
	const stage = event?.target?.getStage?.()
	const pointer = stage?.getPointerPosition?.()
	if (!props.panEnabled && pointer) {
		emit('canvas-pointerup', {
			x: (pointer.x - cameraX.value) / cameraScale.value,
			y: (pointer.y - cameraY.value) / cameraScale.value
		})
	}
	isPanning.value = false
}

const onTouchMove = (event: any) => {
	const touch1 = event.evt.touches[0]
	const touch2 = event.evt.touches[1]

	if (touch1 && !touch2) {
		onPanMove(event)
		touchState.lastCenter = null
		touchState.lastDistance = 0
		return
	}

	if (!touch1 || !touch2) {
		return
	}

	event.evt.preventDefault()
	isPanning.value = false

	const center = getTouchCenter(touch1, touch2)
	const distance = getTouchDistance(touch1, touch2)

	if (!touchState.lastCenter) {
		touchState.lastCenter = center
		touchState.lastDistance = distance
		return
	}

	const oldScale = cameraScale.value
	const pointTo = {
		x: (center.x - cameraX.value) / oldScale,
		y: (center.y - cameraY.value) / oldScale
	}

	const scaleByDistance = distance / touchState.lastDistance
	const nextScale = clampScale(oldScale * scaleByDistance)

	const dx = center.x - touchState.lastCenter.x
	const dy = center.y - touchState.lastCenter.y

	cameraScale.value = nextScale
	cameraX.value = center.x - pointTo.x * nextScale + dx
	cameraY.value = center.y - pointTo.y * nextScale + dy

	touchState.lastCenter = center
	touchState.lastDistance = distance
}

const onTouchEnd = (event: any) => {
	touchState.lastCenter = null
	touchState.lastDistance = 0
	onPanEnd(event)
}

// Public methods for external zoom control
const performZoom = (direction: 1 | -1, point?: { x: number; y: number }) => {
	const centerPoint = point || {
		x: stageWidth.value / 2,
		y: stageHeight.value / 2
	}
	const nextScale = clampScale(cameraScale.value * (direction > 0 ? zoom.step : 1 / zoom.step))
	zoomAtPoint(centerPoint, nextScale)
}

const resetZoom = () => {
	cameraX.value = 0
	cameraY.value = 0
	cameraScale.value = 1
}

const focusWorldBounds = (
	bounds: { minX: number; minY: number; maxX: number; maxY: number },
	padding = 72
) => {
	const safeStageWidth = Math.max(1, stageWidth.value)
	const safeStageHeight = Math.max(1, stageHeight.value)

	const contentWidth = Math.max(1, bounds.maxX - bounds.minX)
	const contentHeight = Math.max(1, bounds.maxY - bounds.minY)

	const availableWidth = Math.max(1, safeStageWidth - padding * 2)
	const availableHeight = Math.max(1, safeStageHeight - padding * 2)

	const scale = clampScale(Math.min(availableWidth / contentWidth, availableHeight / contentHeight))
	const centerX = (bounds.minX + bounds.maxX) / 2
	const centerY = (bounds.minY + bounds.maxY) / 2

	cameraScale.value = scale
	cameraX.value = safeStageWidth / 2 - centerX * scale
	cameraY.value = safeStageHeight / 2 - centerY * scale
}

defineExpose({
	performZoom,
	resetZoom,
	focusWorldBounds
})

const getGridLineStart = (worldStart: number, gridSize: number, gridOffset: number) => {
	return Math.floor((worldStart - gridOffset) / gridSize) * gridSize + gridOffset
}

const worldBounds = computed(() => ({
	left: (0 - cameraX.value) / cameraScale.value,
	right: (stageWidth.value - cameraX.value) / cameraScale.value,
	top: (0 - cameraY.value) / cameraScale.value,
	bottom: (stageHeight.value - cameraY.value) / cameraScale.value
}))

const verticalLines = computed<GridLine[]>(() => {
	const lines: GridLine[] = []
	const { left, right, top, bottom } = worldBounds.value
	const pitchX = props.gridSizeX || props.gridSize
	const first = getGridLineStart(left, pitchX, props.gridOffsetX)

	for (let x = first; x <= right + pitchX; x += pitchX) {
		lines.push({
			points: [x, top, x, bottom]
		})
	}

	return lines
})

const horizontalLines = computed<GridLine[]>(() => {
	const lines: GridLine[] = []
	const { left, right, top, bottom } = worldBounds.value
	const pitchY = props.gridSizeY || props.gridSize
	const first = getGridLineStart(top, pitchY, props.gridOffsetY)

	for (let y = first; y <= bottom + pitchY; y += pitchY) {
		lines.push({
			points: [left, y, right, y]
		})
	}

	return lines
})

watch([cameraX, cameraY, cameraScale, stageWidth, stageHeight], () => {
	scheduleCameraChangeEmit()
}, { immediate: true })
</script>

<template>
	<div ref="hostEl" class="grid-root">
		<template v-if="isMounted">
			<v-stage
				:config="{ width: stageWidth, height: stageHeight, listening: true }"
				@mousedown="onPointerDown"
				@mousemove="onPanMove"
				@mouseup="onPanEnd"
				@mouseleave="onPanEnd"
				@touchstart="onPointerDown"
				@wheel="onWheel"
				@touchmove="onTouchMove"
				@touchend="onTouchEnd"
			>
				<v-layer>
					<v-rect :config="{ x: 0, y: 0, width: stageWidth, height: stageHeight, fill: background, name: 'grid-background', perfectDrawEnabled: false }" />
				</v-layer>

				<v-layer :config="{ x: cameraX, y: cameraY, scaleX: cameraScale, scaleY: cameraScale }">
					<template v-if="showGridVisible">
						<v-line
							v-for="(line, index) in verticalLines"
							:key="`v-${index}`"
							:config="{
								points: line.points,
								stroke: majorStroke,
								strokeWidth: 1.2,
								strokeScaleEnabled: false,
								listening: false,
								perfectDrawEnabled: false
							}"
						/>

						<v-line
							v-for="(line, index) in horizontalLines"
							:key="`h-${index}`"
							:config="{
								points: line.points,
								stroke: majorStroke,
								strokeWidth: 1.2,
								strokeScaleEnabled: false,
								listening: false,
								perfectDrawEnabled: false
							}"
						/>
					</template>

					<slot />
				</v-layer>
			</v-stage>
		</template>
	</div>
</template>

<style scoped>
.grid-root {
	/* Fill whatever space the flex layout actually gives this element (the
	   canvas area below the toolbar), not the raw viewport. Using vw/vh here
	   ignored the toolbar's height, so the measured stage size was taller than
	   what was actually visible, throwing off focusWorldBounds()'s centering. */
	width: 100%;
	height: 100%;
	overflow: hidden;
	touch-action: none;
}
</style>
